import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton, Tooltip } from '@kontentino/ui';
import { modalRoutes } from 'constants/routes';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { useAnalyticsContext } from 'app/modules/analytics/context/AnalyticsContext';
import { getSortedAnalyticsPerformancePagesFromGroupPages } from 'app/modules/analytics/utils/profileGroupPageToPageData';
import { isSelectableGroupPage } from 'app/modules/analytics/utils/analyticsGroupProfileSelection';
import ProfileListRow from 'app/modules/analytics/components/sidebar/ProfileListRow';
import ProfileListSection from 'app/modules/analytics/components/sidebar/ProfileListSection';
import { ANALYTICS_MAX_SELECTED_GROUP_PROFILES } from 'app/modules/analytics/config/analyticsConfig';
import { buildAnalyticsManageProfileGroupSearch } from 'app/modules/analytics/utils/buildAnalyticsManageProfileGroupSearch';

const AnalyticsSidebarGroupProfileList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedProfileGroup, enabledGroupPageIds, toggleGroupPageId } =
    useAnalyticsContext();

  const groupPages = useMemo(
    () =>
      getSortedAnalyticsPerformancePagesFromGroupPages(
        selectedProfileGroup?.pages,
      ),
    [selectedProfileGroup?.pages],
  );

  const isSelectionLimitReached =
    enabledGroupPageIds.size >= ANALYTICS_MAX_SELECTED_GROUP_PROFILES;
  const selectionLimitTooltip = t('analytics.maxSelectedProfilesReached', {
    limit: ANALYTICS_MAX_SELECTED_GROUP_PROFILES,
  });
  const disconnectedTooltip = t('analytics.profileDisconnectedNotAnalysed');
  const addProfileLabel = t('addProfile');

  return (
    <ProfileListSection
      title={t('group', { defaultValue: 'Group' })}
      scrollClassName="tw-max-h-[500px]"
      titleAction={
        <Tooltip content={addProfileLabel}>
          <span className="tw-inline-flex">
            <IconButton
              variant="ghost"
              size="small"
              icon={<Icon icon={faPlus} />}
              onClick={() =>
                navigate({
                  hash: modalRoutes.MANAGE_PROFILE_GROUP,
                  search: buildAnalyticsManageProfileGroupSearch(
                    selectedProfileGroup?.id,
                  ),
                })
              }
              data-cy="analytics_sidebar_add-profile"
              aria-label={addProfileLabel}
            />
          </span>
        </Tooltip>
      }
    >
      {groupPages.map((page) => {
        const isChecked = enabledGroupPageIds.has(page.id);
        const isDisconnected = !isSelectableGroupPage(page);
        const isDisabled =
          isDisconnected || (isSelectionLimitReached && !isChecked);
        const disabledTooltip = isDisconnected
          ? disconnectedTooltip
          : selectionLimitTooltip;

        return (
          <ProfileListRow
            key={page.id}
            variant="selectable"
            page={page}
            checked={isChecked && !isDisconnected}
            onToggle={() => toggleGroupPageId(page.id)}
            isDisabled={isDisabled}
            disabledTooltip={isDisabled ? disabledTooltip : undefined}
          />
        );
      })}
    </ProfileListSection>
  );
};

export default AnalyticsSidebarGroupProfileList;
